import { RfxTab } from "newui/features/Rfx/types/component-types";

export interface UsersPaths {
	list: string;
}

export interface AccountSettingsPaths {
	activityLog: string;
	plansAndBilling: string;
	security: string;
	userManagement: string;
	users: UsersPaths;
}

export interface CompanyProfilePaths {
	corporateDocs: string;
	information: string;
	keyPersonnel: string;
	keyPersonnelAdd: string;
	officeLocations: string;
	projectsAndExperience: string;
	sharedProfiles: string;
	confirmVerification: (documentId: number, ocrJobId: number) => string;
	keyPersonnelEdit: (id: number) => string;
}

export interface RfxProjectPaths {
	all: string;
	archived: string;
	favourites: string;
	my: string;
	new: string;
}

export interface RfxClientPaths {
	all: string;
	// current: string;
	// evaluation: string;
	// drafts: string;
	// archived: string;
	createTemplate: string;
	manageTemplate: string;
}

export interface RfxSupplierPaths {
	accepted: string;
	all: string;
	awarded: string;
	submitted: string;
	unsuccessful: string;
}

export interface SupplierListPaths {
	my: string;
	new: string;
	panel: string;
}

export interface MySuppliersPaths {
	list: string;
	rate: string;
}

export enum PreAuthenticationPaths {
	LOGIN = "login",
	REGISTER = "register",
}

export abstract class BasePaths {
	basePath: string;
	home: string;

	constructor(basePath: string) {
		const cleanBasePath = basePath.trim();
		this.basePath = cleanBasePath.endsWith("/")
			? cleanBasePath.slice(0, cleanBasePath.length - 1)
			: cleanBasePath;
	}

	protected joinPaths(leftPath: string, rightPath: string): string {
		const left = leftPath.trim().endsWith("/")
			? leftPath.trim().slice(0, leftPath.length - 1)
			: leftPath.trim();
		const right = rightPath.trim().startsWith("/")
			? rightPath.trim().slice(1, rightPath.length)
			: rightPath.trim();

		return `${left}/${right}`;
	}

	protected prefixWithBasePath(path: string): string {
		return this.joinPaths(this.basePath, path);
	}
}

const companyProfileBase = "company-profile";

export abstract class BaseMemberPaths extends BasePaths {
	companyProfile: CompanyProfilePaths;
	accountSettings: AccountSettingsPaths;
	myProfile: string;
	previewProfile: string;
	rfxSettings: string;
	pdfSettings: string;

	constructor(companyId: number) {
		super(`/account/${companyId}/`);

		this.accountSettings = {
			activityLog: this.prefixWithBasePath("account-settings"),
			plansAndBilling: this.prefixWithBasePath("account-settings"),
			security: this.prefixWithBasePath("account-settings?tab=Security"),
			userManagement: this.prefixWithBasePath("account-settings"),
			users: {
				list: this.prefixWithBasePath("account-settings/users"),
			},
		};

		this.rfxSettings = this.prefixWithBasePath("rfx-settings");
		this.pdfSettings = this.prefixWithBasePath("pdf-settings");

		const keyPersonnelBase = `${companyProfileBase}/resources`;
		this.companyProfile = {
			corporateDocs: this.prefixWithBasePath(`${companyProfileBase}/documents`),
			information: this.prefixWithBasePath(
				`${companyProfileBase}/company-information`
			),
			keyPersonnel: this.prefixWithBasePath(keyPersonnelBase),
			keyPersonnelAdd: this.prefixWithBasePath(`${keyPersonnelBase}/add`),
			keyPersonnelEdit: (id: number) =>
				this.prefixWithBasePath(`${keyPersonnelBase}/${id}/edit`),
			officeLocations: this.prefixWithBasePath(
				`${companyProfileBase}/company-locations`
			),
			projectsAndExperience: this.prefixWithBasePath(
				`${companyProfileBase}/projects`
			),
			sharedProfiles: this.prefixWithBasePath(
				`${companyProfileBase}/shared-companies`
			),
			confirmVerification: (documentId: number, ocrJobId: number) =>
				this.prefixWithBasePath(
					`${companyProfileBase}/documents/${documentId}/confirm-verification?ocr_job=${ocrJobId}`
				),
		};

		this.myProfile = this.prefixWithBasePath("edit-user-profile");

		this.previewProfile = `company/${companyId}/projects`;

		this.home = this.companyProfile.information;
	}

	viewRfxSubmission(
		rfxId: number,
		submissionId: number,
		tabId: RfxTab | null = null,
		contextId: number | string | null = null
	) {
		const basePath = this.prefixWithBasePath(
			`rfx/${rfxId}/submission/${submissionId}`
		);

		if (!tabId) {
			return `${basePath}/returnable-schedules`;
		}

		return `${basePath}/${tabId}/${contextId ? contextId : ""}`;
	}
}
